// Design tokens for the v2 (mobile-friendly) form input library.
//
// Deliberately not modeled on the current app's inputs: a small neutral
// palette, one accent color used sparingly (focus/selected state only). The
// brand green is kept as that one accent (src/app/App.css `:root
// --greenia-color`) — that's the identity, not the dated part.
//
// `border` was originally a very light grey (#d0d5dd, ~1.6:1 contrast
// against the white field background) — reads as "no border at all" for
// anyone with reduced contrast sensitivity (low vision, cataracts, older
// eyes generally). Darkened to match `textMuted` (~4.6:1, comfortably past
// WCAG's 3:1 minimum for UI component boundaries) so the field edge is
// legible on its own, not just via the resting shadow.
export const v2Colors = {
  primary: '#00a651',
  primaryHover: '#00934a',
  primarySoft: 'rgba(0, 166, 81, 0.08)',
  primarySoftHover: 'rgba(0, 166, 81, 0.16)',
  primaryBorder: 'rgba(0, 166, 81, 0.4)',
  // Support/help accent — used only for "Contatta il Backoffice". Kept off
  // green (Procedi/Consulenza) and red (danger/badge) so it reads as its own
  // action.
  info: '#1d4ed8',
  infoSoft: 'rgba(29, 78, 216, 0.08)',
  infoSoftHover: 'rgba(29, 78, 216, 0.16)',
  infoBorder: 'rgba(29, 78, 216, 0.35)',
  danger: '#d92d20',
  dangerSoft: 'rgba(217, 45, 32, 0.06)',
  dangerSoftHover: 'rgba(217, 45, 32, 0.14)',
  dangerBorder: 'rgba(217, 45, 32, 0.4)',
  // Matches the amber gradient already used elsewhere in the app
  // (src/common/components/elements/ContractDocuments.jsx) for the
  // background/border; text is a darker shade of the same amber for
  // readability (the gradient's own lighter tone is too light for text).
  // Darkening amber shifts its hue toward red/brown the further down the
  // scale you go, so this stays close to the gradient's own darker stop
  // rather than going further, to keep it reading as orange.
  warning: '#f59e0b',
  warningHover: '#d97706',
  warningText: '#c2750a',
  warningTextHover: '#a5640a',
  warningSoft: 'rgba(245, 158, 11, 0.08)',
  warningSoftHover: 'rgba(245, 158, 11, 0.16)',
  warningBorder: 'rgba(245, 158, 11, 0.4)',
  text: '#101828',
  textMuted: '#667085',
  textFaint: '#98a2b3',
  border: '#667085',
  borderStrong: '#344054',
  inputBackground: '#ffffff',
  surface: '#f9fafb',
  // A disabled field previously only swapped to `surface`, which is barely
  // distinguishable from the white background at a glance — darkened here
  // (and given its own dashed-looking border tone) so "not editable yet"
  // reads clearly without relying on hover/cursor to notice.
  disabledSurface: '#eef1f4',
  disabledBorder: '#c3c9d1',
};

// Matches the breakpoint convention already dominant across the app
// (e.g. src/screens/HouseProduct/components/stepForms/DatiAssicuratoStep.styles.js)
export const v2Breakpoints = {
  tablet: '900px',
  desktop: '1200px',
};

// App-wide font stack: Inter first (standard UI font across fintech/insurtech,
// tabular figures for clean price alignment), falling back to each OS's own UI
// font when the webfont isn't loaded. Also used by the pages that used to be
// Poppins, so the whole app renders the same font.
export const v2FontFamily = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

// Placeholder AND typed-value text in inputs/selects use each OS's own UI
// font instead (no extra webfont to load, and these are specifically tuned
// by Apple/Microsoft/Google for on-screen legibility at small sizes).
export const v2ReadableFontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
